/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/react';
import axios, { AxiosHeaders } from 'axios';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { APP_SETTINGS } from '@constants/constants';
import useToast from '@contexts/useToast';
import routes from '@router/routes';

import { ERROR_TOAST_BY_CODE } from '@components/Toast/consts';
import { ToastType } from '@customTypes/toast';
import { useKeycloak } from '@hooks/useKeycloak';
import useAuth from './useAuth';

const instance = axios.create({
  baseURL: APP_SETTINGS.apiurl,
});

export const updateAxiosToken = (token: string | undefined): boolean => {
  if (token) {
    instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete instance.defaults.headers.common.Authorization;
  }
  return !!token;
};

export const AxiosInterceptor = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { pathname } = useLocation();
  const { onInvalidAuth, onSessionExpired } = useAuth();

  const { keycloak } = useKeycloak();
  const keycloakRef = useRef(keycloak);

  // Error responses trigger a redirect to the error page
  const canRedirectToErrorPage = useRef<boolean>(true);

  // Update ref synchronously so it's available before child useEffects run
  useLayoutEffect(() => {
    keycloakRef.current = keycloak;
  }, [keycloak]);

  const [isSet, setIsSet] = useState(false);

  useEffect(() => {
    const route = routes.find(route => matchPath(route.path, pathname));
    canRedirectToErrorPage.current = !route?.preventAPIErrorRedirect;
  }, [pathname]);

  useEffect(() => {
    const requestInterceptor = instance.interceptors.request.use(
      async config => {
        const currentKeycloak = keycloakRef.current;

        // If user is designated as authenticated by Keycloak
        if (currentKeycloak?.authenticated) {
          try {
            await currentKeycloak.updateToken(30);
          } catch (error) {
            Sentry.captureException(error);
            onSessionExpired();
          }

          // Proceed to check for token existence even if refresh fails.
          if (!currentKeycloak.token) {
            const error = new Error('Authenticated user missing token after refresh attempt');
            Sentry.captureException(error, {
              extra: {
                url: config.url,
                authenticated: currentKeycloak.authenticated,
                tokenParsed: currentKeycloak.tokenParsed,
              },
            });
            return Promise.reject(error);
          }

          if (config.headers) {
            (config.headers as AxiosHeaders).Authorization = `Bearer ${currentKeycloak.token}`;
          }
        }

        return config;
      },
      error => Promise.reject(error),
    );
    const responseInterceptor = instance.interceptors.response.use(
      (res: any) => {
        if (res.data.status_code === 401) {
          onInvalidAuth();
        } else {
          return res;
        }
      },
      (error: any) => {
        const errorCode = error.response?.status ?? 0;
        const rejectedResponse = Promise.reject(error.response);

        if (errorCode === 401) {
          onInvalidAuth();
          return rejectedResponse;
        }

        if (axios.isCancel(error)) {
          return rejectedResponse;
        }

        if (errorCode !== 400) {
          Sentry.captureException(error);
        }

        if (error.config?.skipResponseInterceptor) {
          return rejectedResponse;
        }

        if (canRedirectToErrorPage.current && ![408, 409, 500, 503].includes(errorCode)) {
          navigate('/error', {
            state: { errorCode },
          });
        } else {
          const errorData = ERROR_TOAST_BY_CODE[errorCode] ?? { type: ToastType.CUSTOM_ACTIONS };
          addToast(errorData);
        }

        return rejectedResponse;
      },
    );

    queueMicrotask(() => {
      setIsSet(true);
    });

    return () => {
      instance.interceptors.response.eject(responseInterceptor);
      instance.interceptors.request.eject(requestInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isSet) return null;

  return <>{children}</>;
};

export default instance;

export const useRequestSaver = () => {
  const requestSaverList = useRef<Record<string, AbortController>>({});

  return useMemo(() => {
    const abortAllRequests = (): void => {
      Object.values(requestSaverList.current).forEach(requestSaver => {
        if (!requestSaver.signal.aborted) {
          requestSaver.abort();
        }
      });

      requestSaverList.current = {};
    };

    const abortRequest = (id: string): void => {
      const request = requestSaverList.current[id];

      if (!request) {
        return;
      }

      if (!request.signal.aborted) {
        request.abort();
      }

      delete requestSaverList.current[id];
    };

    const getAbortSignal = (id: string): AbortSignal => {
      if (requestSaverList.current[id]) {
        abortRequest(id);
      }

      const abortController = new AbortController();
      requestSaverList.current[id] = abortController;
      return abortController.signal;
    };

    return {
      abortAllRequests,
      abortRequest,
      getAbortSignal,
    };
  }, []);
};
